import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';
import extractText from 'flarum/common/utils/extractText';

import ConfirmEmailNotice from './ConfirmEmailNotice';
import { MEETUPS_TAG_SLUG, emailConfirmed } from '../utils/routeState';
import {
  conflictMessage,
  emptyValues,
  isEmailUnconfirmedConflict,
  mapServerErrors,
  meetupLimits,
  meetupOptions,
  validate,
  FIELD_ORDER,
  type Errors,
  type FieldName,
  type FormValues,
  type MeetupLimits,
  type MeetupOptions,
} from '../utils/proposeForm';

/**
 * M6 · «Proponer un encuentro» — cinco campos y, al enviar, una solicitud PRIVADA para el
 * equipo (T-FORO-V14 revierte la decisión E de T11: ya no se publica un hilo).
 *
 * POR QUÉ UNA PÁGINA PROPIA Y NO EL FEED. Hasta T11 el CTA de la landing llevaba a
 * `/t/encuentros`: una lista de hilos con el botón «Nueva discusión» del core, es decir, un
 * título y una caja de texto vacía. El mockup M6 pide cinco preguntas concretas — tema, tipo de
 * sesión, lugar, ponente y por qué —, que son justo las que hacen comparable una propuesta con
 * otra. Esa estructura no cabe en un editor libre.
 *
 * SOLO CON SESIÓN, igual que el Form B y por una razón parecida: quien propone tiene que quedar
 * identificado para que el equipo pueda responderle — una solicitud sin autor no sirve de nada.
 * Al invitado se le manda al alta (que desde T11 es pública y directa), no se le pinta un
 * formulario que fallaría al final.
 *
 * Estados: invitado · cuenta sin confirmar · formulario · enviada. SIN «función no
 * configurada» desde T-FORO-V14: una solicitud privada no depende de ninguna categoría.
 */
export default class ProponerPage extends Page {
  values!: FormValues;
  errors: Errors = {};
  unmapped: string[] = [];
  loading = false;
  options!: MeetupOptions;
  limits!: MeetupLimits;

  /** Solicitud recién enviada. Con contenido = estado «enviada». */
  published: any = null;

  /**
   * El servidor acaba de contestar 409 `umbral_email_unconfirmed` (PKG-FORO-PRODUCT-GATES · D).
   *
   * Red del fail-open de `emailConfirmed()`: si `isEmailConfirmed` no viajaba en el payload la
   * pantalla dejo enviar, y el veto llegó del servidor. Gemelo de `SolicitudPage`.
   */
  rejectedAsUnconfirmed = false;

  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitle(extractText(app.translator.trans('umbral-comunidad.forum.proponer.meta_title')));
    app.setTitleCount(0);

    this.options = meetupOptions();
    this.limits = meetupLimits();
    this.values = emptyValues();
  }

  view() {
    return (
      <div className="CmPropose-container container">
        <div className="CmPropose">{this.body()}</div>
      </div>
    );
  }

  body() {
    // El éxito manda sobre todo lo demás: tras enviar, esta pantalla es la confirmación.
    if (this.published) return this.done();
    if (!app.session.user) return this.guest();
    /*
      CUENTA SIN CONFIRMAR — no es un caso raro desde T11, es el estado normal de cualquiera
      que acabe de darse de alta.

      `flarum/core` `src/User/User.php:747-755`, con su propio comentario: «If a user's account
      hasn't been activated, they are essentially no more than a guest» — con el alta pública
      (decisión D de T11) no hay doorkey con `activates=1` que confirme de paso, así que quien
      viene de `/registro` llega aquí sin confirmar hasta que abre su correo. Desde
      T-FORO-V14 el envío ya no exige un permiso de categoría (cualquier cuenta REGISTRADA
      puede proponer) pero sigue exigiendo estar registrado, y una cuenta sin confirmar el
      core la trata como invitada — de ahí que este aviso se mantenga.

      Se dice ANTES de pintar el formulario. Rellenar cinco campos para que el envío conteste
      «tu cuenta todavía no puede enviar solicitudes» es la misma trampa que `RegistroPage`
      evita con su estado «solo por invitación».
    */
    if (!emailConfirmed() || this.rejectedAsUnconfirmed) return this.unconfirmed();

    return this.form();
  }

  // --- estados ------------------------------------------------------------------------

  guest() {
    const t = (key: string) => this.t(key);

    return (
      <div className="CmPropose-state" data-cm-state="guest">
        <span className="CmPropose-ic">✎</span>
        <h1 className="CmPropose-title">{t('guest_title')}</h1>
        <p className="CmPropose-sub">{t('guest_body')}</p>

        <Link className="Button Button--primary CmPropose-submit" href={app.route('umbral-comunidad.registro')}>
          {t('guest_cta')}
        </Link>
        <Button className="Button Button--link CmPropose-secondary" onclick={() => app.modal.show(LogInModal)}>
          {t('guest_login')}
        </Button>
      </div>
    );
  }

  /** Cuenta creada pero sin confirmar: el foro la trata como invitada (ver `body()`). */
  unconfirmed() {
    return (
      <div className="CmPropose-state" data-cm-state="unconfirmed">
        {/*
          T12 · objetivo 2 — este era el sitio donde el bloqueo se NOTA: la persona ya escribio
          una propuesta en la cabeza y se encuentra con que no puede publicarla.

          T11 dejaba aqui un texto sin salida. Ahora va el mismo aviso reutilizable que el exito
          del alta, con el reenvio: es la unica accion que desbloquea de verdad, y tenerla a
          mano justo donde aparece el muro evita el viaje a la bandeja para no encontrar nada.
          El titulo se mantiene el especifico de esta pagina («…para publicar»), que dice mejor
          que el generico QUE es lo que esta bloqueado.
        */}
        <ConfirmEmailNotice heading="h1" title={this.t('unconfirmed_title')} body={this.t('unconfirmed_body')} />
        {this.backToMeetups()}
      </div>
    );
  }

  /**
   * La solicitud ya llegó al equipo (T-FORO-V14).
   *
   * SIN ENLACE A UN HILO: a diferencia de T11, la solicitud nunca se publica, así que no hay
   * un recurso propio que enseñar — la confirmación es el mensaje mismo, y el único camino de
   * vuelta es a la categoría de encuentros públicos (para ver qué ya está agendado).
   */
  done() {
    const t = (key: string) => this.t(key);

    return (
      <div className="CmPropose-state" data-cm-state="published">
        <span className="CmPropose-ic">✓</span>
        <h1 className="CmPropose-title">{t('done_title')}</h1>
        <p className="CmPropose-sub">{t('done_body')}</p>
        {this.backToMeetups()}
      </div>
    );
  }

  backToMeetups() {
    return (
      <Link className="Button Button--link CmPropose-secondary" href={app.route('tag', { tags: MEETUPS_TAG_SLUG })}>
        {this.t('back_to_meetups')}
      </Link>
    );
  }

  // --- formulario ---------------------------------------------------------------------

  form() {
    const t = (key: string) => this.t(key);
    const errorCount = Object.keys(this.errors).length;

    return (
      // `novalidate` por lo mismo que en los otros dos formularios: con la validación nativa
      // activa el navegador se queda el `submit` y no hay barra de errores ni foco al primero.
      <form className="CmPropose-form" novalidate={true} onsubmit={this.onsubmit.bind(this)}>
        <h1 className="CmPropose-title">{t('title')}</h1>
        <p className="CmPropose-sub">{t('subtitle')}</p>

        {errorCount > 0 && (
          <div className="CmPropose-errorbar" role="alert" data-cm-errorbar={String(errorCount)}>
            {app.translator.trans('umbral-comunidad.forum.proponer.error_bar', { count: errorCount })}
          </div>
        )}
        {this.unmapped.map((message) => (
          <div className="CmPropose-errorbar" role="alert">
            {message}
          </div>
        ))}

        {this.text('topic', t('label_topic'), { required: true, hint: t('hint_topic') })}
        {this.select('format', t('label_format'), this.options.format)}
        {/* TEXTO LIBRE desde T-FORO-V14 (antes un `<select>` de cinco ciudades): el pedido es
            «comuna + lugar concreto», que no cabe en una lista cerrada. */}
        {this.text('place', t('label_place'), { required: true, hint: t('hint_place') })}
        {this.text('speaker', t('label_speaker'), { hint: t('hint_speaker') })}
        {this.reason()}

        {/* Nunca deshabilitado (misma nota del mockup M2b): siempre explica qué falta. */}
        <Button className="Button Button--primary CmPropose-submit" type="submit" loading={this.loading}>
          {t('submit')}
        </Button>

        <p className="CmPropose-legal">{t('legal')}</p>
      </form>
    );
  }

  text(name: FieldName, label: any, opts: { required?: boolean; hint?: any } = {}) {
    const error = this.errors[name];

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        <label htmlFor={'cm-prop-' + name}>
          {label}
          {opts.required && <span className="CmRegister-req"> *</span>}
        </label>
        <input
          id={'cm-prop-' + name}
          className="FormControl CmRegister-input"
          name={name}
          type="text"
          value={this.values[name]}
          disabled={this.loading}
          aria-invalid={error ? 'true' : undefined}
          oninput={(e: any) => this.set(name, e.target.value)}
        />
        {opts.hint && !error && <p className="CmRegister-hint">{opts.hint}</p>}
        {this.message(name)}
      </div>
    );
  }

  select(name: FieldName, label: any, values: string[]) {
    const error = this.errors[name];

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        <label htmlFor={'cm-prop-' + name}>
          {label}
          <span className="CmRegister-req"> *</span>
        </label>
        <select
          id={'cm-prop-' + name}
          className="FormControl CmRegister-select"
          name={name}
          disabled={this.loading}
          aria-invalid={error ? 'true' : undefined}
          value={this.values[name]}
          onchange={(e: any) => this.set(name, e.target.value)}
        >
          <option value="">{this.t('select_empty')}</option>
          {values.map((value) => (
            <option value={value}>{app.translator.trans(`umbral-comunidad.lib.options.${name}.${value}`)}</option>
          ))}
        </select>
        {this.message(name)}
      </div>
    );
  }

  reason() {
    const error = this.errors.reason;
    const left = this.limits.reasonMax - this.values.reason.length;

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field="reason">
        <label htmlFor="cm-prop-reason">
          {this.t('label_reason')}
          <span className="CmRegister-req"> *</span>
        </label>
        <textarea
          id="cm-prop-reason"
          className="FormControl CmPropose-textarea"
          name="reason"
          rows={4}
          /* `maxlength` frena al escribir; la validación sigue existiendo porque un cliente que
             no sea este navegador puede mandar lo que quiera. */
          maxlength={this.limits.reasonMax}
          disabled={this.loading}
          value={this.values.reason}
          oninput={(e: any) => this.set('reason', e.target.value)}
        />
        <p className="CmRegister-hint" data-cm-reason-left={String(left)}>
          {app.translator.trans('umbral-comunidad.forum.proponer.hint_reason', { count: left })}
        </p>
        {this.message('reason')}
      </div>
    );
  }

  message(name: FieldName) {
    const error = this.errors[name];

    if (!error) return null;

    return (
      <p className="CmRegister-msg" data-cm-error={name}>
        {error}
      </p>
    );
  }

  set(name: FieldName, value: string) {
    this.values[name] = value;
    this.clear(name);
  }

  clear(name: FieldName) {
    if (this.errors[name] !== undefined) delete this.errors[name];
  }

  // --- envío --------------------------------------------------------------------------

  onsubmit(e: SubmitEvent) {
    e.preventDefault();

    if (this.loading) return;

    this.unmapped = [];
    this.errors = validate(this.values, this.limits);

    if (Object.keys(this.errors).length) {
      this.focusFirstError();
      return;
    }

    this.loading = true;

    app
      .request<any>({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/umbral/meetup-requests',
        body: { data: { attributes: this.payload() } },
        errorHandler: this.onerror.bind(this),
      })
      .then(
        (payload: any) => {
          this.loading = false;

          // `pushPayload` sólo se usa aquí para confirmar que el POST terminó bien — a
          // diferencia de T11 no hay `slug` que leer después, la solicitud nunca se publica.
          this.published = payload && payload.data ? app.store.pushPayload(payload) : null;

          m.redraw();
        },
        () => {
          this.loading = false;
          m.redraw();
        }
      );
  }

  payload() {
    return {
      topic: this.values.topic.trim(),
      format: this.values.format,
      place: this.values.place,
      // `null` y no cadena vacía: es opcional, y el servidor distingue los dos.
      speaker: this.values.speaker.trim() || null,
      reason: this.values.reason.trim(),
    };
  }

  onerror(error: any) {
    // Primero soltar el candado: `requestErrorCatch` del core llama a este manejador ANTES de
    // rechazar la promesa, y con `loading` en true los campos se rinden deshabilitados —
    // `.focus()` sobre un input deshabilitado no hace nada (trampa que ya costó una corrida en
    // T07 y otra en T08).
    this.loading = false;

    const response = error && error.response;

    /*
      Correo sin confirmar: CAMBIO DE PANTALLA, no un mensaje en la barra (PKG-FORO-PRODUCT-
      GATES · D). El escudo preventivo de `body()` es fail-open a propósito, así que este 409
      puede llegar de verdad; sin esta rama caía en la alerta genérica de Flarum («¡Caramba!»)
      igual que en Form B.
    */
    if (isEmailUnconfirmedConflict(response)) {
      this.rejectedAsUnconfirmed = true;
      this.errors = {};
      this.unmapped = [];

      // Gemelo de `SolicitudPage`: la alerta explica por qué la pantalla acaba de cambiar.
      const message = conflictMessage(response);
      if (message) app.alerts.show({ type: 'warning' }, message);

      m.redraw();

      return;
    }

    /*
      El resto de conflictos de estado (hoy sólo el 429 de abuso) se traducen por `code` con el
      gemelo de `requestForm.conflictMessage`, en vez de comparar el string a mano aquí: era la
      única página con esa comparación suelta y por eso se quedó sin traducir el 409 de arriba.
    */
    const conflict = conflictMessage(response);

    if (conflict) {
      this.errors = {};
      this.unmapped = [conflict];
      m.redraw();

      return;
    }

    // Fuera de esos dos, todo error que llegue aquí es de campo (o genérico si no mapea).
    const mapped = mapServerErrors(response);

    this.errors = mapped.errors;
    this.unmapped = mapped.unmapped;

    if (!Object.keys(this.errors).length && !this.unmapped.length) {
      if (error && error.alert) app.alerts.show(error.alert, error.alert.content);
      return;
    }

    this.focusFirstError();
  }

  /** Foco al primer error EN EL ORDEN DEL FORMULARIO, no en el que llegaron (M2b). */
  focusFirstError() {
    m.redraw.sync();

    const first = FIELD_ORDER.find((name) => this.errors[name] !== undefined);

    if (!first) return;

    const wrapper = document.querySelector(`[data-cm-field="${first}"]`);
    const focusable = wrapper && (wrapper.querySelector('input, select, textarea') as HTMLElement | null);

    if (focusable) focusable.focus();
    if (wrapper) wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  t(key: string) {
    return app.translator.trans('umbral-comunidad.forum.proponer.' + key);
  }
}
